/**
 * Copyright 2019 CANAL+ Group
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { merge, of } from "rxjs";
import { map, mergeMap, reduce, tap } from "rxjs/operators";
import { SegmentPipelinesManager } from "../../../../../core/pipelines";
import noop from "../../../../../utils/noop";
import { IndexDBError, SegmentConstuctionError } from "../../utils";
import ContentManager from "../context/ContentsManager";
import { ContentType } from "../context/types";
import EMETransaction from "../drm/keySystems";
import { manifestLoader } from "./manifest";
import { handleSegmentPipelineFromContexts } from "./segment";
/**
 * This function take care of downloading the init segment for VIDEO/AUDIO/TEXT
 * buffer type.
 * Then, he also find out the nextSegments we have to download.
 *
 * @param IInitSettings - Argument we need to start the download.
 * @param IDBPDatabase - An instance of the IndexDB to store the init segment in base
 * @returns An observable
 *
 */
export function initDownloader$(_a, db) {
    var contentID = _a.contentID, url = _a.url, adv = _a.adv, transport = _a.transport, keySystems = _a.keySystems;
    return manifestLoader(url, transport).pipe(mergeMap(function (_a) {
        var manifest = _a.manifest, transportPipelines = _a.transportPipelines;
        var segmentPipelinesManager = new SegmentPipelinesManager(transportPipelines, {
            lowLatencyMode: false,
        });
        var contentManager = new ContentManager(manifest, adv !== undefined ? adv.quality : undefined);
        return of(contentManager.getContextsForCurrentSession()).pipe(mergeMap(function (globalCtx) {
            var _a = contentManager.getContextsFormatted(globalCtx), video = _a.video, audio = _a.audio, text = _a.text;
            return merge(handleSegmentPipelineFromContexts(video, ContentType.VIDEO, {
                segmentPipelinesManager: segmentPipelinesManager,
                isInitData: true,
                type: "start",
            }), handleSegmentPipelineFromContexts(audio, ContentType.AUDIO, {
                segmentPipelinesManager: segmentPipelinesManager,
                isInitData: true,
                type: "start",
            }), handleSegmentPipelineFromContexts(text, ContentType.TEXT, {
                segmentPipelinesManager: segmentPipelinesManager,
                isInitData: true,
                type: "start",
            }));
        }), reduce(function (acc, curr) { return (__spreadArrays(acc, [curr])); }, []), mergeMap(function (initSegments) {
            if (keySystems !== undefined && Object.keys(keySystems).length > 0) {
                EMETransaction(keySystems, {
                    contentID: contentID,
                    initSegments: initSegments.filter(function (_a) {
                        var contentType = _a.contentType;
                        return contentType !== "text";
                    }),
                }, db).subscribe(noop);
            }
            return of.apply(void 0, initSegments);
        }), tap(function (_a) {
            var ctx = _a.ctx, chunkData = _a.chunkData, contentType = _a.contentType;
            var representationID = ctx.representation.id;
            var time = ctx.segment.time;
            db.put("segments", {
                contentID: contentID,
                segmentKey: "init--" + representationID + "--" + contentID,
                data: chunkData,
                size: chunkData.byteLength,
            }).catch(function (err) {
                throw new IndexDBError("\n              " + contentID + ": Impossible to store the current INIT\n              segment (" + contentType + ") at " + time + ": " + err.message + "\n            ");
            });
        }), map(function (_a) {
            var ctx = _a.ctx, contentType = _a.contentType;
            var durationForCurrentPeriod = ctx.period.duration;
            if (durationForCurrentPeriod === undefined) {
                throw new SegmentConstuctionError("\n              Impossible to get future video segments for " + contentType + " buffer,\n              the duration should be an valid integer but: " + durationForCurrentPeriod + "\n            ");
            }
            var nextSegments = ctx.representation.index.getSegments(0, durationForCurrentPeriod);
            return {
                nextSegments: nextSegments,
                ctx: ctx,
                segmentPipelinesManager: segmentPipelinesManager,
                contentType: contentType,
            };
        }));
    }), reduce(function (acc, _a) {
        var nextSegments = _a.nextSegments, _b = _a.ctx, period = _b.period, adaptation = _b.adaptation, representation = _b.representation, manifest = _b.manifest, contentType = _a.contentType, segmentPipelinesManager = _a.segmentPipelinesManager;
        acc.progress.overall += nextSegments.length;
        acc[contentType].push({
            nextSegments: nextSegments,
            period: period,
            adaptation: adaptation,
            representation: representation,
            id: representation.id,
        });
        return __assign(__assign({}, acc), { segmentPipelinesManager: segmentPipelinesManager, manifest: manifest });
    }, {
        progress: { percentage: 0, current: 0, overall: 0 },
        video: [],
        audio: [],
        text: [],
        segmentPipelinesManager: null,
        manifest: null,
        type: "start",
    }));
}
